import blankAvatar from '@/assets/images/blank-avatar-300x300.png';
import { BookIcon } from '@/assets/SVGs/icons/BookIcon';
import AppAvatar from '@/components/ui/AppAvatar';
import { Skeleton } from '@/components/ui/Skeleton';
import EnvToggle from '@/components/EnvToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosPatchRequest, axiosDeleteRequest } from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Link, useNavigate, useLocation } from 'react-router';
import * as jose from 'jose';
import { Bell, LogOut, Menu, CheckCheck, Trash2, TicketIcon, UserIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { disconnectSocket } from '@/socket';
import { useSocketContext } from '@/services/SocketContext';
import { clearFcmToken } from '@/services/fcmService';

interface NavbarProps {
  onMenuToggle?: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/users': 'Users',
  '/users/profile': 'User Profile',
  '/books': 'Books',
  '/categories': 'Categories',
  '/subcategories': 'Subcategories',
  '/tickets': 'Tickets',
  '/analysis/users': 'User Analytics',
  '/analysis/books': 'Book Analytics',
  '/analysis/tickets': 'Ticket Analytics',
  '/notifications': 'Notifications',
  '/settings': 'Settings',
};

function getPageTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (/^\/books\/[^/]+$/.test(pathname)) return 'Book Details';
  if (/^\/tickets\/[^/]+$/.test(pathname)) return 'Ticket Details';
  if (pathname.startsWith('/users/profile')) return 'User Profile';
  return 'Dashboard';
}

/** MongoDB ObjectId pattern: 24 hex chars */
const MONGO_ID_RE = /\b([0-9a-f]{24})\b/i;


/**
 * Derives the navigation URL from a notification.
 *
 * Strategy:
 * 1. Combine ALL string values in the notification into one searchable blob
 * 2. Determine entity type from `type` field or from title/body keywords
 * 3. Extract a 24-char Mongo ID from any field (excluding the notification's own _id)
 * 4. Navigate to /entity/:id if found, else the entity list page
 */
function getNotificationRoute(notification: any): string {
  // Log full object so we can see what the backend actually sends
  console.log('[Nav] Notification full payload:', JSON.stringify(notification, null, 2));

  const notifId: string = notification?._id ?? '';

  // ── Collect all string values from the notification object ─────────
  const allStrings: string[] = [];
  const collectStrings = (obj: any, depth = 0) => {
    if (depth > 3 || !obj) return;
    for (const val of Object.values(obj)) {
      if (typeof val === 'string' && val !== notifId) allStrings.push(val);
      else if (typeof val === 'object') collectStrings(val, depth + 1);
    }
  };
  collectStrings(notification);
  const blob = allStrings.join(' ').toLowerCase();

  // ── Determine entity type ──────────────────────────────────────────
  const typeField = (
    notification?.type || notification?.entityType || notification?.category || ''
  ).toLowerCase();

  const isBook   = typeField.includes('book')   || blob.includes('book') || blob.includes('pending');
  const isTicket = typeField.includes('ticket')  || typeField.includes('report') ||
                   blob.includes('ticket')        || blob.includes('report');
  const isUser   = typeField.includes('user')    || (blob.includes('user') && !isBook && !isTicket);

  // ── Extract entity ID from any field except the notification's own _id ─
  let entityId = '';
  // Priority named fields first
  const PRIORITY_KEYS = ['bookId','book_id','ticketId','ticket_id','entityId','relatedId','ref_id','targetId'];
  for (const key of PRIORITY_KEYS) {
    const val = notification?.[key];
    if (typeof val === 'string' && MONGO_ID_RE.test(val)) { entityId = val.match(MONGO_ID_RE)![1]; break; }
    if (typeof val === 'object' && val !== null) {
      const nested = val._id || val.id;
      if (typeof nested === 'string' && MONGO_ID_RE.test(nested)) { entityId = nested; break; }
    }
  }
  // Then scan all string values (includes body text — some backends embed IDs there)
  if (!entityId) {
    for (const s of allStrings) {
      const m = s.match(MONGO_ID_RE);
      if (m) { entityId = m[1]; break; }
    }
  }

  console.log('[Nav] type:', typeField, '| isBook:', isBook, '| isTicket:', isTicket, '| entityId:', entityId || '(none)');

  if (isBook)   return entityId ? `/books/${entityId}`   : '/books';
  if (isTicket) return entityId ? `/tickets/${entityId}` : '/tickets';
  if (isUser)   return entityId ? `/users/profile/?uid=${entityId}` : '/users';

  return '/notifications';
}

function Navbar({ onMenuToggle }: NavbarProps) {
  const router = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setUnreadCount } = useSocketContext();

  const token = localStorage.getItem('token');
  const { fullName, role }: { fullName: string; role: string } = token
    ? jose.decodeJwt(token)
    : { fullName: '', role: '' };

  const pageTitle = getPageTitle(location.pathname);

  const { data: adminNotifications, isLoading: isLoadingNotifications } = useQuery({
    queryKey: [endpoints.getAdminNotifications],
    queryFn: () => axiosGetRequest(endpoints.getAdminNotifications),
    select: data => {
      // Debug: log the full raw shape of first notification so we can see ALL fields
      if (data?.result?.[0]) {
        console.log('[Notifications] Full object shape:', JSON.stringify(data.result[0], null, 2));
        console.log('[Notifications] All keys:', Object.keys(data.result[0]));
      }
      return data.result;
    },
  });

  // API returns { status, statusCode, unreadCount: N } — NO result wrapper
  const { data: unreadData } = useQuery({
    queryKey: [endpoints.getUnreadCount],
    queryFn: () => axiosGetRequest(endpoints.getUnreadCount),
    refetchInterval: 30_000,
  });

  const unreadCount: number =
    typeof unreadData?.unreadCount === 'number' ? unreadData.unreadCount : 0;

  const markAsRead = useMutation({
    mutationFn: (id: string) => axiosPatchRequest(endpoints.markAsRead, {}, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => axiosPatchRequest(endpoints.markAllAsRead),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
      toast.success('All notifications marked as read');
    },
  });

  const deleteNotification = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.deleteNotification, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
    },
  });

  const getNotificationIcon = (notification: any) => {
    const type = (notification?.type || notification?.title || '').toLowerCase();
    if (type.includes('ticket') || type.includes('report'))
      return <TicketIcon className="h-4 w-4 text-orange-500" />;
    if (type.includes('user'))
      return <UserIcon className="h-4 w-4 text-blue-500" />;
    return <BookIcon className="h-4 w-4 text-primary" />;
  };

  function handleNotificationClick(notification: any) {
    if (!notification?.isRead) markAsRead.mutate(notification._id);
    const route = getNotificationRoute(notification);
    router(route);
  }

  return (
    <nav className="flex h-14 items-center justify-between border-b border-coal-300 bg-white px-4 sm:px-6">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="flex h-8 w-8 items-center justify-center rounded-md text-coal-500 transition-colors hover:bg-coal-100 hover:text-coal-700 lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 className="text-base font-semibold text-coal-900 sm:text-lg">{pageTitle}</h1>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <EnvToggle />

        {/* Notifications */}
        <DropdownMenu
          onOpenChange={open => {
            if (open) {
              setUnreadCount(0);
              queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
            }
          }}
        >
          <DropdownMenuTrigger className="focus:outline-none">
            <div className="relative flex h-8 w-8 items-center justify-center rounded-md text-coal-500 transition-colors hover:bg-coal-100 hover:text-coal-700">
              <Bell className="h-[18px] w-[18px]" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow-sm">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[340px] p-0 sm:w-[400px]">
            <div className="flex items-center justify-between border-b border-coal-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-coal-900">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {unreadCount}
                  </span>
                )}
              </div>
              {(adminNotifications?.length ?? 0) > 0 && (
                <button
                  onClick={() => markAllAsRead.mutate()}
                  className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary transition-colors hover:bg-primary-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>
            <ul className="max-h-[24rem] overflow-auto p-1.5">
              {isLoadingNotifications ? (
                <>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="mb-1 flex items-center gap-3 rounded-lg p-2.5">
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-3/4 rounded" />
                        <Skeleton className="h-3 w-1/2 rounded" />
                      </div>
                    </div>
                  ))}
                </>
              ) : (adminNotifications?.length ?? 0) === 0 ? (
                <div className="flex flex-col items-center py-8">
                  <Bell className="mb-2 h-8 w-8 text-coal-300" />
                  <p className="text-sm text-coal-400">No notifications</p>
                </div>
              ) : (
                adminNotifications?.map((notification: any) => (
                  <li
                    key={notification._id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`group mb-0.5 flex cursor-pointer items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-coal-50 ${
                      !notification?.isRead ? 'bg-red-50/60' : ''
                    }`}
                  >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-coal-100">
                      {getNotificationIcon(notification)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium capitalize text-coal-800 leading-tight">
                        {notification?.title}
                      </h4>
                      <p className="line-clamp-1 text-xs text-coal-400 mt-0.5">
                        {notification?.body}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex items-center gap-1">
                        {!notification?.isRead && (
                          <span className="block h-2 w-2 rounded-full bg-red-500" />
                        )}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            deleteNotification.mutate(notification._id);
                          }}
                          className="rounded p-0.5 text-coal-300 opacity-0 transition-all hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="whitespace-nowrap text-[10px] text-coal-400">
                        {moment(notification?.createdAt).fromNow()}
                      </span>
                    </div>
                  </li>
                ))
              )}
            </ul>
            {(adminNotifications?.length ?? 0) > 0 && (
              <div className="border-t border-coal-200 p-2">
                <Link
                  to="/notifications"
                  className="block rounded-md py-1.5 text-center text-xs font-medium text-primary transition-colors hover:bg-primary-50"
                >
                  View all notifications
                </Link>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Logout */}
        <button
          onClick={() => {
            disconnectSocket();
            clearFcmToken();
            localStorage.clear();
            router('/login', { replace: true });
          }}
          className="flex h-8 w-8 items-center justify-center rounded-md text-coal-500 transition-colors hover:bg-danger-50 hover:text-danger"
        >
          <LogOut className="h-[18px] w-[18px]" />
        </button>

        {/* User */}
        <div className="ml-1 hidden items-center gap-2.5 border-l border-coal-300 pl-3 sm:flex">
          <AppAvatar size="sm" src={blankAvatar} />
          <div className="flex flex-col">
            <Link
              to="/users/profile"
              className="text-sm font-medium text-coal-800 transition-colors hover:text-primary"
            >
              {fullName}
            </Link>
            <span className="text-xs text-coal-400">{role}</span>
          </div>
        </div>
      </div>
    </nav>
  );
}
export default Navbar;
